/**
 * Query Invalidation Helpers
 *
 * Centralized helpers for invalidating React Query caches by entity and tenant.
 * These replace scattered inline `invalidateQueries` / `refetchQueries` calls
 * across individual hook files.
 *
 * ## Design Principles
 *
 * 1. **Single responsibility**: `invalidateQueries` already refetches active
 *    observers — never pair it with a redundant `refetchQueries` call.
 * 2. **Tenant-scoped**: Every helper takes a `tenantId` so invalidation only
 *    affects the current tenant's cache.
 * 3. **Hierarchical**: Use the broadest key that makes sense — e.g.
 *    `queryKeys.cases.all(tenantId)` invalidates all case sub-keys.
 *
 * @example
 * ```tsx
 * const queryClient = useQueryClient();
 * const tenantId = useTenantId();
 *
 * const mutation = useMutation({
 *   mutationFn: (data) => api.createCase(data),
 *   onSuccess: () => {
 *     invalidateCases(queryClient, tenantId);
 *   },
 * });
 * ```
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys, TENANT_SCOPED_ENTITIES } from "./queryKeys";

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

/** Invalidate all case queries for a tenant (inbox, detail, dashboards, etc.) */
export function invalidateCases(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.cases.all(tenantId) });
}

/** Invalidate a single case detail + all list/inbox queries */
export function invalidateCaseAndList(
  queryClient: QueryClient,
  tenantId: number,
  caseId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.detail(tenantId, caseId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.inbox(tenantId, {}),
    // Use "inbox" prefix match so any filter variant is invalidated
    exact: false,
  });
}

/** Invalidate dashboard metrics */
export function invalidateCaseDashboards(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.dashboard(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.managerDashboard(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.overdue(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.dueToday(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.dueThisWeek(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/** Invalidate all rule queries for a tenant */
export function invalidateRules(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.rules.all(tenantId) });
}

/** Invalidate a single rule detail + all list queries + history + statistics */
export function invalidateRuleAndList(
  queryClient: QueryClient,
  tenantId: number,
  ruleId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.rules.detail(tenantId, ruleId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.rules.lists(tenantId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.rules.history(tenantId, ruleId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.rules.statistics(tenantId),
  });
}

/** Invalidate rule categories */
export function invalidateRuleCategories(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.ruleCategories.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/** Invalidate all comments for a specific case */
export function invalidateComments(
  queryClient: QueryClient,
  tenantId: number,
  caseId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.comments.lists(tenantId),
  });
  // Also invalidate the parent case detail (comment count may change)
  queryClient.invalidateQueries({
    queryKey: queryKeys.cases.detail(tenantId, caseId),
  });
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

/** Invalidate all attachments for a specific case */
export function invalidateAttachments(
  queryClient: QueryClient,
  tenantId: number,
  caseId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.attachments.list(tenantId, caseId),
  });
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Invalidate all document library queries */
export function invalidateDocuments(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.documents.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Document Types
// ---------------------------------------------------------------------------

/** Invalidate all document type queries */
export function invalidateDocumentTypes(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.documentTypes.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

/** Invalidate all prompt queries */
export function invalidatePrompts(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.prompts.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Processing Jobs
// ---------------------------------------------------------------------------

/** Invalidate all processing job queries */
export function invalidateProcessingJobs(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.processingJobs.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

/** Invalidate all subscription queries for a tenant */
export function invalidateSubscriptions(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.subscriptions.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Node Responsibilities (Rule Default Assignments)
// ---------------------------------------------------------------------------

/** Invalidate all node responsibility queries */
export function invalidateNodeResponsibilities(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.nodeResponsibilities.all(tenantId),
  });
}

/** Invalidate node responsibility for a specific rule + related queries */
export function invalidateNodeResponsibilityForRule(
  queryClient: QueryClient,
  tenantId: number,
  ruleId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.nodeResponsibilities.detail(tenantId, ruleId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.nodeResponsibilities.effectiveAssignee(
      tenantId,
      ruleId,
    ),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.nodeResponsibilities.history(tenantId, ruleId),
  });
  queryClient.invalidateQueries({
    queryKey: queryKeys.nodeResponsibilities.lists(tenantId),
  });
}

// ---------------------------------------------------------------------------
// Questionnaires
// ---------------------------------------------------------------------------

/** Invalidate all questionnaire queries */
export function invalidateQuestionnaires(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({
    queryKey: queryKeys.questionnaires.all(tenantId),
  });
}

// ---------------------------------------------------------------------------
// User Data (users + roles)
// ---------------------------------------------------------------------------

/** Invalidate all user and role queries for a tenant */
export function invalidateUserData(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.users.all(tenantId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(tenantId) });
}

/** Invalidate user lists only */
export function invalidateUsers(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.users.all(tenantId) });
}

/** Invalidate role lists only */
export function invalidateRoles(
  queryClient: QueryClient,
  tenantId: number,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(tenantId) });
}

// ---------------------------------------------------------------------------
// Tenant Management (admin-scoped)
// ---------------------------------------------------------------------------

/** Invalidate all tenant management queries (admin only) */
export function invalidateTenants(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.tenants.all() });
}

// ---------------------------------------------------------------------------
// Tenant Switch — Full Cache Clear
// ---------------------------------------------------------------------------

/**
 * Remove all tenant-scoped query data for a specific tenant.
 *
 * Call this when an admin switches tenants to ensure zero cross-tenant
 * data leakage (ISO 27001 A.9.1.2 — data segregation).
 *
 * Uses `removeQueries` (not `invalidateQueries`) so data is fully purged
 * rather than marked stale — prevents even momentary display of old tenant data.
 */
export function removeAllTenantData(
  queryClient: QueryClient,
  tenantId: number,
): void {
  for (const entity of TENANT_SCOPED_ENTITIES) {
    queryClient.removeQueries({ queryKey: [entity, tenantId] });
  }
}

/**
 * Clear the entire React Query cache.
 *
 * Use on logout or when a full reset is needed.
 */
export function clearAllCacheData(queryClient: QueryClient): void {
  queryClient.clear();
}
